import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { budgetReviewApi } from '../budgetReviewApi';
import type { BudgetReviewPlanListItem } from '../budgetReviewTypes';

/**
 * The Budget Review register — every plan at a stage the review can act on.
 *
 * **It lists itself; nothing is created here.** A plan reaches this register by closing its
 * own submission window and leaves it by being approved or rejected, so the list is a VIEW
 * over the budget plans rather than a second record. That is the whole reason the module has
 * a read key and no create one, no delete and no Clear data: there is nothing here to make
 * and nothing to empty.
 *
 * **A Draft plan and one whose window is still open are absent**, and their tabs are too. The
 * register offers the four stages it can act on and no more — a Draft tab on a reviewer's
 * register is a control that can only ever read zero, and opening a window is another
 * module's act under another module's key.
 */
export function BudgetReviewIndexPage() {
  const navigate = useNavigate();

  const fetchPlans = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      budgetReviewApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          fiscalYear: query.facets.fiscalYear?.join(','),
        },
        signal,
      ),
    [],
  );

  return (
    <IndexPage<BudgetReviewPlanListItem>
      module="budget-review"
      breadcrumb={['Procurement', 'Investment Planning', 'Budget Review']}
      columns={COLUMNS}
      /*
        TWO state axes. `stage` is where the plan sits in the budget cycle and is what this
        register files by; `status` is the ROW's own live-or-deleted state, which the shell
        reserves, sends at rest and lifts with Show deleted.
      */
      facets={[
        { key: 'stage', label: 'Stage' },
        { key: 'status', label: 'Status' },
        { key: 'fiscalYear', label: 'Fiscal year' },
      ]}
      /*
        Files by STAGE, where the plan list files by fiscal year. A reader there is looking
        for one year's plan; a reviewer is looking for what is waiting on them, which is what
        makes the same folder nav point two different ways on two lists of one record. Either
        reader can repoint it from the rail's own cog.
      */
      folderNavFacet="stage"
      auditEntityTypes={['BudgetPlan', 'BudgetReviewCeiling']}
      rowKey={(plan) => plan.id}
      fetch={fetchPlans}
      searchPlaceholder="Search by plan # or description…"
      emptyMessage="No budget plan is under review. A plan arrives here when its submission window closes."
      onRowOpen={(plan) =>
        navigate(`/procurement/investment-planning/budget-review/${plan.id}`)
      }
      openPermission="budget-review.read"
      /*
        NO Create, NO Delete and NO Clear data, and none of the three is an omission — see the
        page's own remarks. `<IndexPage>` derives multi-select from having something to do to
        a selection, so passing none of them is also what keeps the checkbox column off a list
        nothing can be done to in bulk.
      */
      otherInfo={(plan) => plan}
    />
  );
}

const COLUMNS: ColumnDef<BudgetReviewPlanListItem>[] = [
  {
    /*
      `BUDGET PLAN` — the reference's own header, and its own WIDTH: none, so the column
      flexes and takes the slack the four fixed ones leave.

      **The cell is NOT stacked, and that is the one place this column cannot copy the
      reference.** Its own anchor is a `LinkCell` carrying "FY 2027 Annual Budget Planning"
      over "AIP-2027-0005", and `<StackedCell secondary>` is REFUSED inside an index table
      here — the component logs a development-time error when it renders inside
      `IndexTableSurfaceContext`, because a stacked value has no header, no width, no sort and
      no settings entry, and the list view reads the same columns so the two surfaces would
      silently differ. That guard is enforced rather than advisory, so copying the stack would
      trip it.

      The label it stacks is also derived rather than stored — "FY {year}" — and `FY` states
      the year again one column to the right, which is §8 on one row.
    */
    key: 'planNumber',
    header: 'Budget Plan',
    width: 200,
    // `flex` is how this shell spells the reference's width-less anchor: it absorbs the slack
    // the four fixed columns leave, until the reader drags it.
    flex: true,
    sortable: true,
    // A machine value: mono, never numeric — a code is an identifier, so nothing in it dims.
    render: (plan) => <TextCell value={plan.planNumber} mono />,
  },
  {
    key: 'fiscalYear',
    header: 'FY',
    width: 80,
    sortable: true,
    summary: true,
    // A calendar YEAR is an identifier rather than a quantity — mono without numeric.
    render: (plan) => <TextCell value={String(plan.fiscalYear)} mono />,
  },
  {
    key: 'statusLabel',
    header: 'Stage',
    width: 150,
    sortable: true,
    stage: true,
    /*
      PLAIN TEXT, not a pill, and the column is called Stage. A plan carries two state
      concepts — its lifecycle and whether the row is deleted — and only the second gets the
      pill. Two pills would put one word against two meanings in one row.

      The WORD is the product's own: Finalized, not "Approved". The ACTION on the profile says
      Approve, because that is what a reviewer does; the state it produces has been called
      Finalized since the plan module shipped, and one word for one concept is product-wide.
    */
    render: (plan) => <StatusText tone="inactive">{plan.statusLabel}</StatusText>,
  },
  {
    key: 'ceilingsSet',
    header: 'Priced',
    width: 110,
    align: 'right',
    summary: true,
    /*
      PRICED OUT OF ENROLLED, on one line — the figure the register exists to show. A
      department counts only when EVERY fund it proposed against carries a ceiling, which is
      the same comparison the approval guard makes, so this column and the refusal on Approve
      cannot disagree about which plans are ready.

      Not sortable: the server's allow-list does not name it, and a header whose field the
      server cannot order by falls back to the default sort silently, which reads as a broken
      control.
    */
    render: (plan) => (
      <TextCell value={`${plan.ceilingsSet}/${plan.departmentCount}`} mono numeric />
    ),
  },
  {
    /*
      ONE COLUMN, the reference's `COVERAGE` — the period read as a period.

      It was TWO, `Coverage from` and `Coverage to`. A plan's coverage is one fact with two
      ends, and split across two columns a reader has to hold the first while their eye
      travels to the second. That is the anchor rule's own "merge, never duplicate" reaching a
      pair of dates: one column, one header, one width, and neither end keeps a column of its
      own so nothing is printed twice.

      The cost is stated rather than hidden: the END can no longer be sorted on. Its own order
      was never the useful one — a register of plans is read by when cover STARTS — which is
      the test that rule sets for merging at all.

      A CALENDAR date, so `formatDateOnly` through a mono TextCell — never `<DateCell>`, which
      renders an instant in the viewer's zone and would tell a reader west of UTC the day
      before the one on the record.
    */
    key: 'coverageStartDate',
    header: 'Coverage',
    width: 210,
    sortable: true,
    render: (plan) => (
      <TextCell
        value={`${formatDateOnly(plan.coverageStartDate)} – ${formatDateOnly(plan.coverageEndDate)}`}
        mono
      />
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    /*
      The ROW's own state. A deleted plan is still listed here while the reader has Show
      deleted on, and its marker is the one thing that must never be silent — the register
      cannot delete one, but the plan module can.
    */
    render: (plan) =>
      plan.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
